'use client';

import { useEffect } from 'react';
import MonacoEditor, { Monaco, useMonaco } from '@monaco-editor/react';
import { useTheme } from 'next-themes';

import { useApiV1Ctx } from '@/components/providers/v1-api';

interface EditorProps {
  content: string;
  onContentChange?: (content: string) => void;
  className?: string;
  language?: string;
  lineNumbers?: 'on' | 'off';
}

export function Editor({
  content,
  onContentChange,
  className,
  language = 'markdown',
  lineNumbers = 'on',
}: EditorProps) {
  useSetupTemplateCompletions();
  const { systemTheme } = useTheme();

  return (
    <MonacoEditor
      theme={systemTheme === 'dark' ? 'vs-dark' : 'vs'}
      height='100%'
      width='100%'
      defaultLanguage={language}
      value={content}
      onChange={(value) => onContentChange?.(value ?? '')}
      options={{
        readOnly: !onContentChange,
        wordWrap: 'on',

        /* --- line-number --- */
        lineNumbers: lineNumbers,
        lineNumbersMinChars: 3,
        lineDecorationsWidth: 2,

        /* --- minimap --- */
        minimap: { enabled: false },

        /* --- scroll --- */
        scrollBeyondLastLine: false,

        // don't highlight the line of the cursor
        renderLineHighlight: 'none',
      }}
      className={className}
    />
  );
}

let HAS_SETUP_TEMPLATE_COMPLETIONS = false;

function useSetupTemplateCompletions() {
  const editor = useMonaco();
  const { api } = useApiV1Ctx();
  const { data } = api.agentBlocks.getTemplateContext.useQuery(undefined, {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  useEffect(() => {
    if (!data || !editor) return;

    if (HAS_SETUP_TEMPLATE_COMPLETIONS) return;
    HAS_SETUP_TEMPLATE_COMPLETIONS = true;

    const paths = collectPaths(data);
    registerTemplateCompletions(editor, paths);
  }, [data, editor]);
}

/**
 * Recursively collect “ctx.general.name”-style paths
 * from an example JSON object.
 */
function collectPaths(node: unknown, base: string = ''): string[] {
  if (node === null || typeof node !== 'object') return [base];
  return Object.keys(node).flatMap((k) => collectPaths(node[k as keyof typeof node], base ? `${base}.${k}` : k));
}

function registerTemplateCompletions(editor: Monaco, paths: string[]) {
  editor.languages.registerCompletionItemProvider('markdown', {
    triggerCharacters: ['{', '.'],
    provideCompletionItems: (model, position) => {
      const textBefore = model.getValueInRange(
        new editor.Range(position.lineNumber, 1, position.lineNumber, position.column)
      );

      const m = textBefore.match(/{{\s*([\w.]*)$/);
      if (!m) return { suggestions: [] };

      const pathSoFar = m[1]; // what the user already typed
      const segments = pathSoFar === '' ? [] : pathSoFar.split('.');

      const seen = new Set<string>();
      const suggestions = paths
        .filter((p) => p.startsWith(pathSoFar)) // keep only the sub-tree we are in
        .map((fullPath) => {
          const remaining = fullPath.slice(pathSoFar.length); // ".general.name"
          const next = remaining.replace(/^\./, '').split('.')[0]; // "general"
          if (!next) return null; // already at a leaf
          if (seen.has(next)) return null;
          seen.add(next);
          return {
            label: next,
            kind: editor.languages.CompletionItemKind.Variable,
            insertText: next,
            range: new editor.Range(
              position.lineNumber,
              position.column - (segments.at(-1)?.length ?? 0),
              position.lineNumber,
              position.column
            ),
          };
        })
        .filter((s) => s !== null);

      return { suggestions };
    },
  });
}
